import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import { MdArrowBack } from 'react-icons/md'
import AppTable from '../../shared/AppTable'
import claimsGQL from '../../gql/claims'
import TableSkeletonLoader from '../../common/TableLoading'

function ClaimErrorList() {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const location = useLocation()
  const { filterInput, errorDetails, refreshAt, claimsListReturnState } =
    location.state || {}

  const [errorList, setErrorList] = useState([])
  const [total, setTotal] = useState(0)

  const { loading, data, refetch } = useQuery(
    claimsGQL.GET_ERRORS_LIST_FOR_CLAIMS,
    {
      variables: {
        input: filterInput
      },
      skip: !filterInput,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    }
  )

  useEffect(() => {
    if (filterInput && refreshAt) {
      refetch({ input: filterInput })
    }
  }, [refreshAt, filterInput, refetch])

  useEffect(() => {
    if (data?.getErrorsListForClaims) {
      setErrorList(data.getErrorsListForClaims.data || [])
      setTotal(data.getErrorsListForClaims.total || 0)
    }
  }, [data])

  const FormatDate = date => {
    if (!date) return '---'
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' }
    return new Intl.DateTimeFormat('en-GB', options).format(new Date(date))
  }

  const formatAmount = value =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value ?? 0)

  const columns = [
    {
      header: t('SQ #'),
      render: (row, index) => index + 1
    },
    { header: t('Patient Name'), field: 'patient_name' },
    {
      header: t('Document ID'),
      render: row => row?.document_id || '---'
    },
    { header: t('Payer Name'), field: 'payer_name' },
    {
      header: t('Net'),
      align: 'right',
      render: row => (
        <span className='font-semibold text-[#0070C2] whitespace-nowrap'>
          {formatAmount(row?.amount)}
        </span>
      )
    },
    {
      header: t('Date Ordered'),
      render: row => FormatDate(row?.date_ordered)
    },
    // { header: t('Transaction ID'), field: 'transaction_id' },
    {
      header: t('TPA Name'),
      render: row => row?.tpa_name || '---'
    }
  ]

  const isDispositionList = errorDetails?.mode === 'disposition'
  const listTitle = isDispositionList ? t('Claims for Disposition') : t('Claims with Error Code')
  const listValue = isDispositionList ? errorDetails?.disposition : errorDetails?.error_code
  const listDescription = isDispositionList
    ? `${t('Total Claims')}: ${errorDetails?.count || total || 0}`
    : errorDetails?.error_message
  const dispositionLines = isDispositionList
    ? String(listValue || '')
        .split(/,+\s*/)
        .map(item => item.trim())
        .filter(Boolean)
    : []
  const dispositionText = dispositionLines.join(' ')

  return (
    <div className=''>
      {/* Header */}
      <div className=' flex items-start gap-2'>
        <div
          onClick={() =>
            navigate('/claims', {
              state: {
                ...(claimsListReturnState || {}),
                refreshAt: Date.now()
              }
            })
          }
          className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
        >
          <MdArrowBack
            className={
              isRTL ? 'text-white h-4 w-4 rotate-180' : 'text-white h-4 w-4 '
            }
          />
        </div>
        <div className='min-w-0 flex-1'>
          {isDispositionList ? (
            <div className='rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm'>
              <div className='mb-1 flex flex-wrap items-center justify-between gap-2'>
                <div>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      fontSize: '17px',
                      lineHeight: 1.2
                    }}
                  >
                    {listTitle}
                  </Typography>
                </div>
                <span className='rounded-md bg-[#0070C2]/10 px-2.5 py-1 text-[12px] font-bold text-[#0070C2]'>
                  {listDescription}
                </span>
              </div>
              <div
                className='max-h-[92px] overflow-auto rounded-md bg-slate-100 px-2 py-1'
                title={listValue || ''}
              >
                {dispositionLines.length > 0 ? (
                  <span className='block min-w-max whitespace-nowrap text-[12px] font-medium leading-5 text-slate-700'>
                    {dispositionText}
                  </span>
                ) : (
                  <span className='text-[12px] font-medium text-slate-500'>
                    ---
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div>
              <Typography
                sx={{ fontWeight: 600, color: '#1e293b', fontSize: '18px' }}
              >
                {listTitle}
              </Typography>
              <Typography
                sx={{
                  display: 'block',
                  color: '#0070C2',
                  fontSize: '13px',
                  fontWeight: 700
                }}
              >
                {listValue || '---'}
              </Typography>
              <Typography variant='caption' className='text-slate-500 font-medium'>
                {listDescription}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className='mt-2'>
        <AppTable
          columns={columns}
          data={errorList}
          loading={loading}
          onView={row => {
            navigate('/claims/details', {
              state: {
                currentItem: { transaction_id: row.transaction_id },
                returnTo: '/claims/error-list',
                returnState: {
                  filterInput,
                  errorDetails,
                  claimsListReturnState
                },
                claimsListReturnState
              }
            })
          }}
          onEdit={row => {
            navigate('/claims/edit/form/' + row.transaction_id, {
              state: {
                returnTo: '/claims/error-list',
                returnState: {
                  filterInput,
                  errorDetails,
                  claimsListReturnState
                }
              }
            })
          }}
          LoaderComponent={
            <TableSkeletonLoader columnCount={columns.length} rowCount={10} />
          }
        />
      </div>
    </div>
  )
}

export default ClaimErrorList
